/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {InfoIcon} from '../../icons/current/InfoIcon';
import Render from '../../render/Render';
import {dlsColor} from '../../theme/theme';

export type SelectionIndicatorVariant = 'selected' | 'total';

export interface SelectionIndicatorProps {
  count: number;
  variant?: SelectionIndicatorVariant;
  label?: string;
  showIcon?: boolean;
  className?: string;
}

const useStyles = makeStyles(() => ({
  root : {
    boxSizing       : 'border-box',
    display         : 'inline-flex',
    flexDirection   : 'row',
    flexShrink      : 0,
    alignItems      : 'center',
    gap             : '4px',
    height          : '24px',
    borderRadius    : '4px',
    backgroundColor : dlsColor('aquamarine'),
    color           : dlsColor('darkTeal'),
    fontFamily      : 'Inter',
    fontWeight      : 600,
    fontSize        : '12px',
    lineHeight      : '16px',
    whiteSpace      : 'nowrap'
  },
  selected : {
    padding       : '0 8px',
    letterSpacing : '1px'
  },
  total : {
    justifyContent : 'center',
    minWidth       : '24px',
    padding        : '4px 8px',
    letterSpacing  : '0.5px'
  }
}));

/**
 * SelectionIndicator renders the DLS Status Indicator Count Badge. The
 * `selected` variant shows a user-selected count with its label (e.g. a
 * filter category's "4 Selected"), and the `total` variant shows a
 * system-provided count as a bare number (e.g. an accordion section total).
 * The info icon slot is hidden by default, per the DLS specification.
 *
 * ```typescript
 * import {translate} from '@ion/core/src/locale/locale';
 * import SelectionIndicator from '@ion/dls/src/components/status/SelectionIndicator';
 *
 * ...
 *
 * <SelectionIndicator count={4}
 *   label={translate('filters:drawer.selectedCount', {count: 4})} />
 * <SelectionIndicator variant='total' count={7} />
 * ```
 *
 * @param count     - The count rendered by the badge.
 * @param variant   - Optional badge variant, `selected` (default) or `total`.
 * @param label     - Optional translated text for the `selected` variant,
 *                    falls back to the bare count when omitted.
 * @param showIcon  - Optional flag to render the leading info icon.
 * @param className - Optional class name merged onto the badge root.
 */
export const SelectionIndicator = ({
  count,
  variant = 'selected',
  label,
  showIcon = false,
  className
}: SelectionIndicatorProps) => {
  const classes = useStyles();
  const text    = variant === 'selected' ? (label ?? count) : count;
  return (
    <span className={classnames(classes.root, classes[variant], className)}>
      <Render.If condition={showIcon}>
        <InfoIcon size={16} color={dlsColor('grey3')} />
      </Render.If>
      <span>
        {text}
      </span>
    </span>
  );
};

export default React.memo(SelectionIndicator);
